<template>
  <InfoCard
    checkboxId="extra-info-apply-variants"
    showApplyToVariants
    :applyToVariants="applyToVariants"
    :isSaving="combinedIsSaving"
    :showSuccessState="showSuccessState"
    :isSaved="combinedIsSaved"
    :saveButtonWidth="showSuccessState ? 'auto' : '183px'"
    @saveChanges="handleSaveChanges"
    @update:applyToVariants="$emit('update:applyToVariants', $event)"
  >
    <div class="product-safety-content additional-info">
    <div class="title-area">
      <div class="title">Ek Bilgiler</div>
      <div class="description">Ürün güvenliği ek bilgileri; kullanım talimatı, materyal bileşeni, bakım talimatı, CE uygunluk sembolü, yıkama talimatı, ürün uyarı sembolleri, ürün güvenliği uyarıları, içerik listesi, tamir edilebilirlik bilgilerini içerir.</div>
    </div>
      <div class="product-safety-content-boxes" :class="{ disabled: isProductDisabled }">
      <div class="box">
        <Input
          type="textarea"
          label="Kullanım Talimatı"
          id="usage-instruction"
          v-model:value="formData.usageInstructions"
          :disabled="isProductDisabled"
        />
        <Input
          type="textarea"
          label="Bakım Talimatı"
          id="maintenance-instruction"
          v-model:value="formData.careInstructions"
          :disabled="isProductDisabled"
        />
        <Input
          type="textarea"
          label="Ürün Uyarı Sembolleri"
          id="product-warning-symbols"
          v-model:value="formData.productWarningSymbols"
          :disabled="isProductDisabled"
        />
        <Input
          type="textarea"
          label="İçerik Listesi"
          id="content-list"
          v-model:value="formData.contentList"
          :disabled="isProductDisabled"
        />
        <DropdownSelectable
          ref="ceComplianceDropdownRef"
          label="CE Uygunluk Sembolü"
          id="ce-approval-symbol"
          :dropItems="ceOptions"
          :isRadioGroup="true"
          :isShowSearch="false"
          :preSelectedRadioItem="selectedCEOption"
          :disabled="isProductDisabled"
          @selectedRadioItem="handleCESelect"
          @changedDropdownState="handleDropdownStateChange"
        />
      </div>
      <div class="box">
        <Input
          type="textarea"
          label="Materyal Bileşeni"
          id="material-component"
          v-model:value="formData.materialComponent"
          :disabled="isProductDisabled"
        />
        <Input
          type="textarea"
          label="Yıkama Talimatı"
          id="washing-instruction"
          v-model:value="formData.washingInstructions"
          :disabled="isProductDisabled"
        />
        <Input
          type="textarea"
          label="Ürün Güvenliği Uyarıları"
          id="product-safety-warnings"
          v-model:value="formData.productSafetyWarnings"
          :disabled="isProductDisabled"
        />
        <Input
          type="textarea"
          label="Tamir Edilebilirlik"
          id="repairability"
          v-model:value="formData.repairability"
          :disabled="isProductDisabled"
        />
      </div>
    </div>
  </div>
  </InfoCard>

  </template>

  <script setup>
  import { reactive, watch, ref, computed, nextTick } from 'vue';
  import { useStore } from 'vuex';
  import { DropdownSelectable, Input } from "@n11/so-ui-components";
  import InfoCard from '../InfoCard.vue';
  import { sendProductDetailEvent } from '@/utils/ga4/helper';
  import { GA4_EVENT } from '@/utils/ga4/event';

  const store = useStore();
  const isProductDisabled = computed(() => store.getters['products/isProductDisabled']);

  const ceOptions = [
    { id: 'var', name: 'Var', value: true },
    { id: 'yok', name: 'Yok', value: false }
  ];

  const props = defineProps({
    onSaveChanges: {
      type: Function,
      default: null,
    },
    safetyInfoData: {
      type: Object,
      default: null,
    },
    applyToVariants: {
      type: Boolean,
      default: true,
    },
    isSavingAllChanges: {
      type: Boolean,
      default: false,
    },
  });
  
  const emit = defineEmits(['update:applyToVariants', 'hasChanges']);

  const formData = reactive({
    usageInstructions: '',
    careInstructions: '',
    productWarningSymbols: '',
    contentList: '',
    materialComponent: '',
    washingInstructions: '',
    productSafetyWarnings: '',
    repairability: '',
    ceCompliance: null
  });

  const ceComplianceDropdownRef = ref(null);

  const isSaving = ref(false);
  const showSuccessState = ref(false);
  const isDirty = ref(false);
  const isHydrating = ref(false);

  const combinedIsSaving = computed(() => {
    return isSaving.value || (props.isSavingAllChanges && isDirty.value);
  });

  const combinedIsSaved = computed(() => {
    return !isDirty.value || showSuccessState.value;
  });

  const findCEOptionByValue = (value) => {
    if (value === null || value === undefined) {
      return null;
    }
    return ceOptions.find(option => option.value === value) || null;
  };

  const selectedCEOption = computed(() => {
    return findCEOptionByValue(formData.ceCompliance);
  });

  const handleCESelect = (payload) => {
    formData.ceCompliance = payload?.value ?? null;
    emit('hasChanges');
  };

  const getData = () => {
    const data = {};

    Object.keys(formData).forEach(key => {
      if (formData[key] !== '' && formData[key] !== null) {
        data[key] = formData[key];
      }
    });

    return data;
  };

  const handleDropdownStateChange = async (isOpen) => {
    if (isOpen && ceComplianceDropdownRef.value && formData.ceCompliance != null) {
      await nextTick();
      const selectedOption = findCEOptionByValue(formData.ceCompliance);
      if (selectedOption) {
        const radioButtonGroupRef = ceComplianceDropdownRef.value.$refs?.radioButtonGroup;
        if (radioButtonGroupRef) {
          radioButtonGroupRef.selectedItem = selectedOption;
        }
      }
    }
  };

  const fetchSafetyInfo = async () => {
    if (!props.safetyInfoData) return;

    try {
      const response = { data: props.safetyInfoData };
      if (response?.data?.additionalInfo) {
        isHydrating.value = true;
        Object.assign(formData, response.data.additionalInfo);

        await nextTick();
        if (ceComplianceDropdownRef.value && formData.ceCompliance != null) {
          const selectedOption = findCEOptionByValue(formData.ceCompliance);
          if (selectedOption) {
            ceComplianceDropdownRef.value.selectedRadioItem = selectedOption;
          }
        }
        queueMicrotask(() => {
          isHydrating.value = false;
        });
      }
    } catch (error) {
      console.error('Error fetching safety info:', error);
    }
  };

  const handleSaveChanges = async () => {
  if (!props.onSaveChanges) return;

  try {
    isSaving.value = true;

    await props.onSaveChanges();

    const productDetail = store.getters['products/getProductDetail'];
    sendProductDetailEvent(GA4_EVENT.SAVE_CHANGES, {
      button_name: 'Değişiklikleri Kaydet',
      button_type: 'Save Changes',
      button_area: 'Safety Additional Information',
      item_id: productDetail?.id,
      all_variants_checkbox: props.applyToVariants,
    });

    showSuccessState.value = true;
    isDirty.value = false;

    store.commit(
      "common/SET_TOASTER_DATA",
      {
        type: "success",
        title: "Başarılı",
        description: "Yaptığınız değişiklikler kaydedilmiştir.",
      }
    );
  } catch (error) {
    showSuccessState.value = false;

    const errorMessage =
      error?.response?.data?.message ||
      error?.message ||
      "İşlem sırasında bir hata oluştu. Lütfen tekrar deneyin.";

    store.commit(
      "common/SET_TOASTER_DATA",
      {
        type: "error",
        title: "Hata",
        description: errorMessage,
      }
    );

    throw error;
  } finally {
    isSaving.value = false;
  }
};

  watch(
  formData,
  () => {
    if (isHydrating.value) return;
    isDirty.value = true;
    showSuccessState.value = false;
    emit('hasChanges');
  },
  { deep: true }
);

  watch(
    () => formData.ceCompliance,
    (newValue) => {
      if (!ceComplianceDropdownRef.value) return;

      if (newValue != null) {
        const selectedOption = findCEOptionByValue(newValue);
        if (selectedOption) {
          ceComplianceDropdownRef.value.selectedRadioItem = selectedOption;
        }
      } else {
        ceComplianceDropdownRef.value.selectedRadioItem = null;
      }
    },
    { immediate: false }
  );

  watch(
    () => props.safetyInfoData,
    async (newData) => {
      if (!newData) return;
      
      await fetchSafetyInfo();
      isDirty.value = false;
    },
    { immediate: true }
  );



  defineExpose({
    combinedIsSaved,
    getData,
    showSaveAllSuccessState: () => {
      showSuccessState.value = true;
      isDirty.value = false;
    },
  });
  </script>

  <style lang="scss" scoped>
  @import "@/assets/styles/variables.scss";

  .product-safety-content,
  .box {
    .title-area {
      .title {
        font-size: 14px;
        font-weight: 600;
        line-height: 18px;
        letter-spacing: -0.05px;
        margin-bottom: 8px;
      }

      .description {
        font-size: 11px;
        line-height: 14px;
        height: 30px;
        margin-bottom: 16px;
      }
    }
    &-form-area {
      display: flex;
      flex-direction: column;
      gap: 10px;
      margin: 16px 0px;
      padding: 24px;
      border-radius: 8px;
      border: 1px solid $colorM10;
      background-color: #F8F7FC;
    }
  }
  .box {
    flex: 1;

    div {
      margin-bottom: 10px;
    }

    &:last-child {
      margin-left: 25px;
    }
  }
  .product-safety-content {
    &-boxes {
      display: flex;
      justify-content: space-around;

      &.disabled {
        pointer-events: none;

        :deep(input),
        :deep(textarea),
        :deep(.input-wrapper),
        :deep(.input-container) {
          pointer-events: none;
        }

        :deep(.dropdownSelectable) {
          pointer-events: none;
          
          .dropdownSelectable-btn {
            cursor: not-allowed;
            pointer-events: none;
            background-color: #f6f7f9;
          }
        }
      }
    }
  }
  .error {
    &-text {
      font-size: 11px;
      font-weight: 400;
      line-height: 14px;
      color: $colorE50;
      margin-left: 12px;
    }
  }
  </style>
